import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Activity, BarChart3, CheckCircle2, ChevronDown, ChevronRight,
  CircleAlert, Loader2, RefreshCw, Search, Zap,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid,
} from "recharts";

const errMsg = (e, fallback) => e?.response?.data?.detail || fallback;

const fmtInt = (v) => (v || 0).toLocaleString("en-AU");
const fmtBytes = (bytes) => {
  const b = Number(bytes) || 0;
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

/* ── Per-mapping details drawer ────────────────────────────────── */
function MappingDetail({ mapping, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/awstats/mappings/${mapping.mapping_id}/data`, { params: { days } })
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => toast.error(errMsg(e, "Failed to load AWStats data")))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [mapping.mapping_id, days]);

  const chartData = useMemo(() =>
    (data?.rows || []).map((r) => ({
      date: r.date.slice(5), // MM-DD
      visits: r.visits,
      unique: r.unique_visitors,
      pages: r.pages,
    })), [data]);

  return (
    <div className="mt-2 rounded-md border border-zinc-200 bg-zinc-50/50 p-3 space-y-3"
      data-testid={`awstats-mapping-detail-${mapping.mapping_id}`}>
      <div className="flex items-center gap-2 text-xs">
        <span className="font-semibold">History window:</span>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="h-7 w-32 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="30">Last 30 days</SelectItem>
            <SelectItem value="90">Last 90 days</SelectItem>
            <SelectItem value="180">Last 6 months</SelectItem>
            <SelectItem value="365">Last 12 months</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="ghost" size="sm" onClick={onClose}
          className="ml-auto h-7 text-xs">Close</Button>
      </div>
      {loading ? (
        <div className="text-xs text-zinc-500 flex items-center gap-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />Loading…
        </div>
      ) : (data?.rows || []).length === 0 ? (
        <div className="text-xs text-zinc-500 py-4 text-center">
          No rows stored yet — click <b>Backfill</b> on the mapping row to pull history.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-xs">
            <div className="bg-white rounded border border-zinc-200 p-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Unique visitors</div>
              <div className="text-sm font-bold">{fmtInt(data.totals.unique_visitors)}</div>
            </div>
            <div className="bg-white rounded border border-zinc-200 p-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Visits</div>
              <div className="text-sm font-bold">{fmtInt(data.totals.visits)}</div>
            </div>
            <div className="bg-white rounded border border-zinc-200 p-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Pages</div>
              <div className="text-sm font-bold">{fmtInt(data.totals.pages)}</div>
            </div>
            <div className="bg-white rounded border border-zinc-200 p-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Hits</div>
              <div className="text-sm font-bold">{fmtInt(data.totals.hits)}</div>
            </div>
            <div className="bg-white rounded border border-zinc-200 p-2">
              <div className="text-[10px] uppercase tracking-wide text-zinc-500">Bandwidth</div>
              <div className="text-sm font-bold">{fmtBytes(data.totals.bandwidth)}</div>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" tick={{ fontSize: 10 }} />
              <YAxis tick={{ fontSize: 10 }} tickFormatter={fmtInt} />
              <Tooltip formatter={(v) => fmtInt(v)} />
              <Line type="monotone" dataKey="visits" name="Visits" stroke="#0ea5e9" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="unique" name="Unique" stroke="#22c55e" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="pages" name="Pages" stroke="#a855f7" strokeWidth={1.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </>
      )}
    </div>
  );
}

/* ── Main tab ────────────────────────────────────────────────────── */
export default function HostAwStatsTab() {
  const [mappings, setMappings] = useState([]);
  const [servers, setServers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState({}); // server_id → bool
  const [discoverProgress, setDiscoverProgress] = useState({}); // server_id → {done,total,found}
  const [pulling, setPulling] = useState({}); // mapping_id → bool
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [m, s, p] = await Promise.all([
        api.get("/awstats/mappings"),
        api.get("/hosting/servers"),
        api.get("/awstats/property-picker"),
      ]);
      setMappings(m.data.mappings || []);
      setServers(Array.isArray(s.data) ? s.data : (s.data.servers || []));
      setProperties(p.data.properties || []);
    } catch (e) {
      toast.error(errMsg(e, "Failed to load AWStats data"));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const discoverServer = async (server_id) => {
    setDiscovering((p) => ({ ...p, [server_id]: true }));
    try {
      const { data } = await api.post(`/awstats/discover/${server_id}`);
      // New async pattern: server returns {job_id, status:"running"}
      if (data.job_id) {
        toast.info("Discovery started — polling for progress…");
        const start = Date.now();
        // Poll until done / failed / 15 min cap
        while (true) {
          await new Promise((r) => setTimeout(r, 2500));
          let job;
          try {
            const res = await api.get(`/awstats/discover-jobs/${data.job_id}`);
            job = res.data;
          } catch (pollErr) {
            toast.error("Lost the discovery job (poll failed)");
            break;
          }
          if (job.status === "done") {
            const errN = (job.errors || []).length;
            if (errN > 0) {
              toast.warning(`Discovered ${job.domains_found} domains across ${job.accounts_done}/${job.accounts_total} accounts (${errN} account errors, ${job.newly_added} new)`);
            } else {
              toast.success(`Discovered ${job.domains_found} domains across ${job.accounts_done} accounts (${job.newly_added} new)`);
            }
            setDiscoverProgress((p) => {
              const np = { ...p }; delete np[server_id]; return np;
            });
            await load();
            break;
          }
          if (job.status === "failed") {
            toast.error(job.error || "Discovery failed");
            setDiscoverProgress((p) => {
              const np = { ...p }; delete np[server_id]; return np;
            });
            break;
          }
          if (Date.now() - start > 15 * 60 * 1000) {
            toast.error("Discovery exceeded 15 min — check server logs");
            setDiscoverProgress((p) => {
              const np = { ...p }; delete np[server_id]; return np;
            });
            break;
          }
          // Still running — surface live progress
          setDiscoverProgress((p) => ({
            ...p,
            [server_id]: {
              done: job.accounts_done || 0,
              total: job.accounts_total || 0,
              found: job.domains_found || 0,
            },
          }));
        }
      } else {
        // Legacy synchronous shape (fallback)
        const failedCount = (data.errors || []).length;
        if (failedCount > 0) {
          toast.warning(`Discovered ${data.domains_found} domains across ${data.accounts} accounts (${failedCount} account errors)`);
        } else {
          toast.success(`Discovered ${data.domains_found} domains across ${data.accounts} accounts`);
        }
        await load();
      }
    } catch (e) { toast.error(errMsg(e, "Discovery failed")); }
    finally { setDiscovering((p) => ({ ...p, [server_id]: false })); }
  };

  const updateMapping = async (mapping_id, patch) => {
    try {
      await api.put(`/awstats/mappings/${mapping_id}`, patch);
      await load();
    } catch (e) { toast.error(errMsg(e, "Update failed")); }
  };

  const pullOne = async (mapping_id) => {
    setPulling((p) => ({ ...p, [mapping_id]: true }));
    try {
      const { data } = await api.post(`/awstats/pull/${mapping_id}`);
      if (data.ok) {
        toast.success(`Pulled ${data.daily_rows} daily + ${data.monthly_rows} monthly rows for ${data.domain}`);
      } else {
        toast.error(data.error || "Pull failed");
      }
      await load();
    } catch (e) { toast.error(errMsg(e, "Pull failed")); }
    finally { setPulling((p) => ({ ...p, [mapping_id]: false })); }
  };

  const pullAll = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/awstats/pull");
      if (!data.job_id) {
        toast.info("No enabled + mapped domains to pull");
        setBusy(false);
        return;
      }
      toast.info(`Bulk pull started · ${data.requested} domain(s)`);
      const start = Date.now();
      while (true) {
        await new Promise((r) => setTimeout(r, 3000));
        let job;
        try {
          const res = await api.get(`/awstats/pull-jobs/${data.job_id}`);
          job = res.data;
        } catch (e) {
          toast.error("Lost the pull job (poll failed)");
          break;
        }
        if (job.status === "done") {
          if (job.failed > 0) {
            toast.warning(`Pulled ${job.succeeded}/${job.requested} · ${job.failed} failed`);
          } else {
            toast.success(`Pulled ${job.succeeded} mapped domain(s)`);
          }
          await load();
          break;
        }
        if (job.status === "failed") {
          toast.error("Bulk pull failed");
          break;
        }
        if (Date.now() - start > 30 * 60 * 1000) {
          toast.error("Bulk pull exceeded 30 min — check server logs");
          break;
        }
      }
    } catch (e) { toast.error(errMsg(e, "Bulk pull failed")); }
    finally { setBusy(false); }
  };

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return mappings;
    return mappings.filter((m) =>
      m.domain.includes(q) ||
      (m.whm_user || "").toLowerCase().includes(q) ||
      (m.client_name || "").toLowerCase().includes(q) ||
      (m.business_unit_name || "").toLowerCase().includes(q));
  }, [mappings, filter]);

  const enabledCount = mappings.filter((m) => m.enabled && m.business_unit_id).length;

  return (
    <div className="space-y-4" data-testid="awstats-tab">
      {/* Header */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <Activity className="h-5 w-5 text-amber-600 shrink-0" />
          <div className="flex-1 min-w-[240px]">
            <div className="text-sm font-semibold">AWStats · daily traffic ingest</div>
            <div className="text-xs text-zinc-500">
              Pulls unique visitors, visits, pages, hits, bandwidth from cPanel via
              your existing WHM API tokens — no extra credentials needed.
              &nbsp;<b>{enabledCount}</b> mapped &amp; enabled ·
              <b> {mappings.length}</b> total domain(s) discovered.
              Nightly job runs at 04:15 Sydney.
            </div>
            <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2 inline-block">
              <b>Workflow:</b> 1. Click <b>Discover</b> on a server → 2. <b>Map</b> each domain to a Property below → 3. <b>Enable</b> → 4. <b>Pull</b> (or wait for nightly).
            </div>
          </div>
          <Button size="sm" onClick={pullAll} disabled={busy || enabledCount === 0}
            data-testid="awstats-pull-all-btn">
            {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Zap className="h-4 w-4 mr-1" />}
            Pull all now
          </Button>
        </CardContent>
      </Card>

      {/* Per-server discovery bar */}
      {servers.length > 0 && (
        <Card>
          <CardContent className="p-4 space-y-2">
            <div className="text-sm font-semibold">Discover domains</div>
            <div className="text-xs text-zinc-500 -mt-1">
              Enumerates every domain (main + addon + subdomain + parked) on every
              cPanel account. Run this once per server, and again whenever accounts change.
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
              {servers.map((s) => {
                const prog = discoverProgress[s.server_id];
                return (
                  <Button key={s.server_id} variant="outline" size="sm"
                    onClick={() => discoverServer(s.server_id)}
                    disabled={!!discovering[s.server_id]}
                    data-testid={`awstats-discover-${s.server_id}`}>
                    {discovering[s.server_id]
                      ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                      : <Search className="h-3.5 w-3.5 mr-1" />}
                    {s.host} <span className="text-zinc-400 ml-1">· {s.label || "server"}</span>
                    {prog && (
                      <span className="ml-2 text-[10px] text-zinc-500">
                        · {prog.done}/{prog.total} accts · {prog.found} domains
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Input placeholder="Filter by domain, cPanel user, client, or property…"
          value={filter} onChange={(e) => setFilter(e.target.value)}
          className="max-w-md" data-testid="awstats-filter-input" />
        <Button variant="ghost" size="sm" onClick={load} disabled={loading}
          data-testid="awstats-reload-btn">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </div>

      {/* Mappings table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 text-sm text-zinc-500 text-center">
              <Loader2 className="h-4 w-4 animate-spin inline" /> Loading…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-sm text-zinc-500 text-center">
              {mappings.length === 0
                ? "No domains discovered yet. Click a server above to run discovery."
                : "No mappings match your filter."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b bg-zinc-50">
                  <tr className="text-left text-[10px] uppercase tracking-wide text-zinc-500">
                    <th className="py-2 px-3 w-6"></th>
                    <th className="py-2 px-3">Domain</th>
                    <th className="py-2 px-3">cPanel user</th>
                    <th className="py-2 px-3">Property (business unit)</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((m) => {
                    const isOpen = openId === m.mapping_id;
                    const isMapped = !!m.business_unit_id;
                    const isRunning = !!pulling[m.mapping_id];
                    return (
                      <React.Fragment key={m.mapping_id}>
                        <tr className="border-b last:border-b-0 hover:bg-zinc-50/50"
                          data-testid={`awstats-row-${m.mapping_id}`}>
                          <td className="py-2 px-3">
                            <button onClick={() => setOpenId(isOpen ? null : m.mapping_id)}
                              className="text-zinc-400 hover:text-zinc-700"
                              data-testid={`awstats-row-expand-${m.mapping_id}`}>
                              {isOpen ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                            </button>
                          </td>
                          <td className="py-2 px-3">
                            <div className="font-medium">{m.domain}</div>
                            <div className="text-[10px] text-zinc-500">
                              {m.domain_type} · {m.days_stored || 0}d stored
                              {m.months_stored ? ` · ${m.months_stored}mo` : ""}
                            </div>
                          </td>
                          <td className="py-2 px-3 font-mono text-xs">{m.whm_user}</td>
                          <td className="py-2 px-3">
                            <Select
                              value={m.business_unit_id || "__none__"}
                              onValueChange={(v) => updateMapping(m.mapping_id, {
                                business_unit_id: v === "__none__" ? "" : v,
                              })}>
                              <SelectTrigger className="h-8 text-xs w-full max-w-[240px]"
                                data-testid={`awstats-bu-select-${m.mapping_id}`}>
                                <SelectValue placeholder="— unmapped —" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">— unmapped —</SelectItem>
                                {properties.map((b) => (
                                  <SelectItem key={b.business_unit_id} value={b.business_unit_id}>
                                    {b.name}
                                    {b.client_name && <span className="text-zinc-400"> · {b.client_name}</span>}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="py-2 px-3">
                            {m.last_error ? (
                              <Badge className="bg-red-50 text-red-700 border-red-200 text-[10px]"
                                title={m.last_error}>
                                <CircleAlert className="h-3 w-3 mr-1" />error
                              </Badge>
                            ) : m.enabled && isMapped ? (
                              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px]">
                                <CheckCircle2 className="h-3 w-3 mr-1" />active
                              </Badge>
                            ) : isMapped ? (
                              <Badge variant="outline" className="text-[10px]">mapped, paused</Badge>
                            ) : (
                              <Badge variant="outline" className="text-zinc-400 text-[10px]">unmapped</Badge>
                            )}
                            {m.last_pulled_at && (
                              <div className="text-[10px] text-zinc-400 mt-0.5">
                                {new Date(m.last_pulled_at).toLocaleString("en-AU", {
                                  day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-3 text-right whitespace-nowrap">
                            {isMapped && (
                              <>
                                <Button variant="ghost" size="sm" className="h-7 text-xs"
                                  onClick={() => updateMapping(m.mapping_id, { enabled: !m.enabled })}
                                  data-testid={`awstats-enable-${m.mapping_id}`}>
                                  {m.enabled ? "Pause" : "Enable"}
                                </Button>
                                <Button variant="outline" size="sm" className="h-7 text-xs ml-1"
                                  onClick={() => pullOne(m.mapping_id)}
                                  disabled={isRunning}
                                  data-testid={`awstats-pull-${m.mapping_id}`}>
                                  {isRunning
                                    ? <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                    : <BarChart3 className="h-3 w-3 mr-1" />}
                                  Pull
                                </Button>
                              </>
                            )}
                          </td>
                        </tr>
                        {isOpen && (
                          <tr>
                            <td colSpan={6} className="px-3 pb-3">
                              <MappingDetail mapping={m} onClose={() => setOpenId(null)} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
